import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useInView } from "framer-motion";

export const ORANGE = "#FA582D";

export const Pill = ({ to, href, children, className = "", ghost = false, testid, onClick, type }) => {
  const cls = `group inline-flex items-center justify-center gap-2.5 rounded-full font-medium text-sm sm:text-[15px] px-6 sm:px-7 py-3.5 transition-all duration-300 ${
    ghost ? "border border-white/30 text-white hover:border-nx-orange hover:text-nx-orange" : "bg-nx-orange text-white hover:bg-nx-orangelight hover:-translate-y-0.5"
  } ${className}`;
  const inner = <>{children}<ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>;
  if (to) return <Link to={to} className={cls} data-testid={testid}>{inner}</Link>;
  if (href) return <a href={href} className={cls} data-testid={testid}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
};

export const TextLink = ({ to, children, testid, className = "" }) => (
  <Link to={to} className={`inline-flex items-center gap-2 text-white text-sm sm:text-[15px] font-medium border-b border-white pb-0.5 hover:text-nx-orange hover:border-nx-orange transition-colors ${className}`} data-testid={testid}>
    {children}
  </Link>
);

export const Eyebrow = ({ children, className = "", testid }) => (
  <p className={`text-[11px] sm:text-xs tracking-[0.28em] uppercase text-nx-orange font-medium ${className}`} data-testid={testid}>{children}</p>
);

export const Rule = ({ className = "" }) => (
  <div className={`relative h-6 w-64 max-w-full ${className}`} aria-hidden="true">
    <span className="absolute left-0 top-0 h-6 w-px bg-nx-orange" />
    <span className="absolute left-0 top-0 h-px w-full bg-nx-orange/70" />
  </div>
);

export const H2 = ({ children, className = "", testid }) => (
  <h2 className={`font-outfit font-light text-3xl sm:text-5xl lg:text-[3.6rem] text-white leading-[1.08] tracking-[-0.02em] ${className}`} data-testid={testid}>{children}</h2>
);

export const Card = ({ children, className = "", hatch = true, testid }) => (
  <div className={`relative overflow-hidden rounded-2xl border border-white/15 bg-nx-black2 ${className}`} data-testid={testid}>
    {hatch && <div className="pan-hatch absolute inset-0 pointer-events-none" />}
    <div className="relative">{children}</div>
  </div>
);

export const useCount = (target, active, duration = 1600) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return v;
};

export const Gauge = ({ value, label, sub, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const v = useCount(value, inView);
  const r = 54, c = 2 * Math.PI * r;
  return (
    <div ref={ref} className="flex flex-col items-center text-center" data-testid={testid}>
      <div className="relative w-44 h-44 sm:w-52 sm:h-52">
        <svg viewBox="0 0 128 128" className="w-full h-full -rotate-90">
          <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="4" />
          <motion.circle cx="64" cy="64" r={r} fill="none" stroke="url(#pan-grad)" strokeWidth="9" strokeLinecap="butt" strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={inView ? { strokeDashoffset: c * (1 - value / 100) } : undefined} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }} />
          <defs>
            <linearGradient id="pan-grad" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#7a2a14" />
              <stop offset="100%" stopColor={ORANGE} />
            </linearGradient>
          </defs>
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-outfit font-medium text-4xl sm:text-5xl text-white">{Math.round(v)}<span className="text-2xl ml-1">%</span></span>
      </div>
      <p className="mt-4 font-outfit text-2xl sm:text-3xl text-nx-orange">{label}</p>
      <p className="mt-1 text-sm text-slate-300 max-w-[14rem] leading-snug">{sub}</p>
    </div>
  );
};

export const Bar = ({ value, label, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const v = useCount(value, inView);
  return (
    <div ref={ref} data-testid={testid}>
      <p className="text-xs sm:text-sm tracking-[0.12em] uppercase text-white">{label}</p>
      <div className="mt-3 flex items-center gap-5">
        <div className="flex-1 max-w-md h-4 rounded-full bg-white/5 overflow-hidden">
          {inView && <div className="pan-bar-fill h-full rounded-full bg-gradient-to-r from-[#3a1208] via-[#c2431f] to-nx-orange" style={{ width: `${value}%` }} />}
        </div>
        <span className="font-outfit font-medium text-4xl sm:text-5xl text-white leading-none w-24">{Math.round(v)}%</span>
      </div>
    </div>
  );
};

export const BigStat = ({ value, unit, label, text, testid }) => (
  <Card className="p-7 sm:p-9 h-full" testid={testid}>
    <p className="font-outfit font-medium text-5xl sm:text-6xl text-white leading-none">
      {value}<span className="text-2xl sm:text-3xl ml-2 text-nx-orange">{unit}</span>
    </p>
    <p className="mt-3 font-outfit text-xl sm:text-2xl text-white">{label}</p>
    <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">{text}</p>
  </Card>
);

export const Tabs = ({ items, active, onChange, className = "", testid }) => (
  <div className={`flex gap-2 overflow-x-auto no-scrollbar border-b border-white/10 ${className}`} role="tablist" data-testid={testid}>
    {items.map((it, i) => (
      <button
        key={it}
        role="tab"
        aria-selected={active === i}
        onClick={() => onChange(i)}
        className={`relative shrink-0 whitespace-nowrap px-4 sm:px-6 py-4 text-sm sm:text-base font-medium transition-colors ${active === i ? "text-white" : "text-slate-400 hover:text-white"}`}
        data-testid={`${testid}-${i}`}
      >
        {it}
        {active === i && <motion.span layoutId={`${testid}-ul`} className="absolute left-0 right-0 -bottom-px h-[3px] bg-nx-orange" />}
      </button>
    ))}
  </div>
);
